import {
    StyleFunction,
    useAuth,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    GQApprovalTokenGroupInput,
    useApprovalTokenGroupLazyQuery,
    useApprovalTokenGroupSaveMutation,
} from "graphql-sdk";
import { schemaApprovalTokenGroup } from "lib";
import { useTranslation } from "locales";
import React, { useCallback, useEffect } from "react";
import { View } from "react-native";
import { useHandleMutationError } from "../../../../hooks";
import { RootStackScreenProps } from "../../../../navigation";
import { AdminContainer } from "../../../container";
import { Form } from "../form";

type ScreenProps = RootStackScreenProps<"APPROVAL_TOKEN_EDIT">;

export function ApprovalTokenGroupEditScreen({
    navigation: { navigate },
    route,
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.approval");

    const [t] = useTranslation();

    const [approvalTokenGroupSave] = useApprovalTokenGroupSaveMutation();
    const { handleMutationError } = useHandleMutationError();

    const [getApprovalTokenGroup, approvalTokenGroup] =
        useApprovalTokenGroupLazyQuery({
            fetchPolicy: "no-cache",
        });
    const form = useForm<GQApprovalTokenGroupInput>(
        schemaApprovalTokenGroup,
        null
    );

    const [{ values }, { setDefaultValues }] = form;

    const styles = useThemedStyle(styleFunc);
    const id = route.params.id;

    useEffect(() => {
        if (!id) {
            navigate("APPROVALS");
        } else {
            getApprovalTokenGroup({ variables: { id } });
        }
    }, [getApprovalTokenGroup, navigate, id]);

    useEffect(() => {
        if (
            !approvalTokenGroup.data ||
            !approvalTokenGroup.data.approvalTokenGroup
        ) {
            return;
        }

        setDefaultValues({
            name: approvalTokenGroup.data.approvalTokenGroup.name,
            active: approvalTokenGroup.data.approvalTokenGroup.active,
        });
    }, [approvalTokenGroup.data, setDefaultValues]);

    const handleSave = useCallback(async () => {
        if (
            !approvalTokenGroup ||
            !approvalTokenGroup.data ||
            !approvalTokenGroup.data.approvalTokenGroup ||
            !values
        ) {
            return;
        }

        await handleMutationError(
            async () =>
                await approvalTokenGroupSave({
                    variables: {
                        id: approvalTokenGroup.data!.approvalTokenGroup!.id,
                        approvalTokenGroup: {
                            name: values.name,
                            active: values.active,
                        },
                    },
                }),
            t("common.saved", "Saved"),
            () => {
                navigate("APPROVALS", { refetch: true, tabIndex: 2 });
            }
        );
    }, [
        handleMutationError,
        navigate,
        approvalTokenGroup,
        approvalTokenGroupSave,
        t,
        values,
    ]);

    return (
        <AdminContainer>
            <View>
                <View style={styles.formBox}>
                    <Form
                        form={form}
                        onSubmit={handleSave}
                        submitButton={["common.save", "Save"]}
                    />
                </View>
            </View>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    formBox: {
        maxWidth: theme.dimensions.maxFormWidth,
    },
});
